/**
 * @file ServiceRecordRow.tsx
 * @description Служебная запись в ленте диалога (chat-window-design.md §3;
 * R4.9, R4.15): строка во всю ширину, не пузырь — высота от 32 (длинный
 * текст переносится на вторую строку), подложка
 * `--surface-low`, скругление 8, тонкая пунктирная рамка. Значок 16 в круге
 * 24 цветом вида, у автоматических — пунктирный круг. Нажатие раскрывает
 * подробности («Передал: … · 02.10.2026, 14:31»). Клиент записей не видит.
 */

import { useState } from "react";

import { cn } from "@/lib/utils";
import type { ConversationEventDto } from "../../lib/api-chat";
import { EVENT_TONE_CLASS, chatEventText, chatEventView } from "./chat-event-meta";
import { clockLabel } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";

interface Props {
  event: ConversationEventDto;
  /** Запись о передаче мне — подложка выбранного и полоса слева. */
  highlight?: boolean;
  /** Кратковременная подсветка после перехода из «Истории диалога». */
  flash?: boolean;
}

const SEGMENT_CLASS = {
  verb: "font-medium text-on-surface",
  name: "font-medium text-on-surface",
  link: "text-on-surface-variant",
} as const;

export function ServiceRecordRow({ event, highlight = false, flash = false }: Props) {
  const [open, setOpen] = useState(false);
  const view = chatEventView(event);
  const Icon = view.icon;
  return (
    <div role="note" aria-label={`${chatEventText(event)} — ${CHAT_TEXT.serviceNote}`} data-chat-event={event.id}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "relative flex min-h-8 w-full items-start gap-2 rounded-lg border border-dashed border-outline-variant px-2 py-1 text-left outline-none",
          "transition-colors duration-500 focus-visible:ring-2 focus-visible:ring-ring",
          highlight || flash ? "bg-accent" : "bg-surface-low",
        )}
      >
        {highlight ? <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] rounded-l-lg bg-primary" /> : null}
        <span
          aria-hidden
          className={cn(
            "inline-flex size-6 shrink-0 items-center justify-center rounded-full",
            EVENT_TONE_CLASS[view.tone],
            view.automatic && "border border-dashed border-current bg-transparent",
          )}
        >
          <Icon className="size-4" />
        </span>
        {/* Текст записи переносится на вторую строку, а не обрезается (QA-5 D17). */}
        <span className="min-w-0 flex-1 break-words pt-0.5 text-sm">
          {view.segments.map((s, i) => (
            <span key={i} className={SEGMENT_CLASS[s.kind]}>
              {s.text}
            </span>
          ))}
        </span>
        <span className="shrink-0 pt-0.5 text-xs tabular-nums text-on-surface-variant">{clockLabel(event.at)}</span>
      </button>
      {open ? <p className="px-10 pt-1 text-xs text-on-surface-variant">{view.detail}</p> : null}
    </div>
  );
}
